import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Spin,
  Form,
  Button,
  Tooltip,
  Typography,
  Breadcrumb,
  Descriptions,
  Space,
  Tag,
} from "antd";
import {
  FieldTimeOutlined,
  BorderlessTableOutlined,
  DownloadOutlined,
} from "@ant-design/icons";
import get from "lodash/get";
import map from "lodash/map";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import requireAuth from "../../hocs/requireAuth";
import { getQueryParams } from "../../utilities/getAndOnParams";
import { fetchCompanies } from "../../redux/actions/companyActions";
import { fetchTimeSummaryReport } from "../../redux/actions/reportActions";
import {
  fetchTimeExcelreport,
  fetchTimeSummaryReportPdf,
} from "../../redux/actions/downloadPdfAction";
import ExcelExportButton from "../../components/breakdownSetting/ExcelExportButton";
import TimeSummaryTable from "../../components/reports/timeSummaryReports/timeSummaryTable";
import HeaderSelector from "../../components/reports/timeSummaryReports/headerSelectorTime";
import { SUPER_ADMIN_ROLE_ACCESS, ORGANISATION_ADMIN_ROLE_IDs, ORGANISATION_MANAGER_IDs, ORGANISATION_SUPERVISORS_IDs } from "../../constants/userRoles";


const { Title } = Typography;

const PageBreadcrumb = () => {
  return (
    <Breadcrumb
      style={{ fontSize: "14px" }}
      items={[
        {
          title: <BorderlessTableOutlined style={{ fontSize: "16px" }} />,
        },
        {
          title: (
            <>
              <FieldTimeOutlined style={{ fontSize: "16px" }} />
              <span>Time</span>
            </>
          ),
        },
        {
          title: "Summary Report",
        },
      ]}
    />
  );
};

const OverallStats = ({ shiftMeta }) => {
  return (
    <>
      {map(shiftMeta, (shift) => (
        <Descriptions
          key={get(shift, "name")}
          bordered
          size="small"
          column={3}
          layout="horizontal"
          style={{
            fontSize: 12,
            lineHeight: "20px",
            minWidth: 150,
            margin: 0,
          }}
          styles={{
            label: {
              fontSize: 12,
              fontWeight: "700",
              padding: "10px 10px",
              lineHeight: "20px",
              backgroundColor: "#fafafa",
              whiteSpace: "nowrap",
            },
            content: {
              fontSize: 12,
              padding: "10px 10px",
              lineHeight: "20px",
              whiteSpace: "nowrap",
              color: "white",
            },
          }}
        >
          <Descriptions.Item label={" "} className="bg-primary">
            {get(shift, "name")}
          </Descriptions.Item>
          <Descriptions.Item label="Machine EFF. %" className="bg-stone-700">
            {get(shift, "machine_efficiency")} %
          </Descriptions.Item>
          <Descriptions.Item label="Production EFF. %" className="bg-stone-700">
            {get(shift, "product_efficiency")} %
          </Descriptions.Item>
        </Descriptions>
      ))}
    </>
  );
};

const SummaryWiseReportsScreen = () => {
  const location = useLocation();
  const { company_uuid, template_type } = getQueryParams(location.search);
  const [machinesSummary, setMachinesSummary] = useState({
    data: [],
    meta: undefined,
  });
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const [config, setConfig] = useState(null);
  const [form] = Form.useForm();
  const [filter, setFilters] = useState({
    company: undefined,
    templateType: undefined,
    date: dayjs(),
  });

  const setinitialCompany = (company) => {
    setFilters({
      ...filter,
      templateType: undefined,
      company,
    });

    setConfig({
      ...config,
      companies: [company],
      templates: get(company, "template_access_list"),
    });
    form.setFieldsValue({
      template: undefined,
      Company: get(company, "id"),
    });
  };

  const getCompanies = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((result) => {
        if (result.length === 1) {
          setinitialCompany(result[0]);
        } else {
          setConfig({ companies: result });
        }

        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const getSummary = (query) => {
    setLoading(true);
    query = query
      ? query
      : {
          type: get(filter, "templateType"),
          company_uuid: get(filter, "company.id"),
          date: get(filter, "date").format("YYYY-MM-DD"),
        };
    dispatch(fetchTimeSummaryReport(query))
      .then((result) => {
        setMachinesSummary(result);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  const onDownloadReport = () => {
    setLoading(true);
    const query = {
      type: get(filter, "templateType"),
      company_uuid: get(filter, "company.id"),
      date: get(filter, "date").format("YYYY-MM-DD"),
    };
    dispatch(fetchTimeSummaryReportPdf(query))
      .then((result) => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };
  const onDownloadExcelReport = () => {
    setLoading(true);
    const query = {
      type: get(filter, "templateType"),
      company_uuid: get(filter, "company.id"),
      date: get(filter, "date").format("YYYY-MM-DD"),
    };
    dispatch(fetchTimeExcelreport(query, "time"))
      .then((result) => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };
  const paramsFormHandler = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((companies) => {
        const company = find(companies, ["id", company_uuid]);

        if (!company) {
          message.error("Company not found from URL param.");
          setLoading(false);
          return;
        }

        form.setFieldsValue({
          Company: company_uuid,
          template: parseInt(template_type),
          date: dayjs(),
        });
        setConfig((prev) => ({
          ...prev,
          companies,
          templates: get(company, "template_access_list"),
        }));

        setFilters((prev) => ({
          ...prev,
          company,
          templateType: template_type,
          date: dayjs(),
        }));
        getSummary({
          company_uuid: company_uuid,
          type: template_type,
          date: dayjs().format("YYYY-MM-DD"),
        });
      })
      .catch(() => {
        message.error(FAILED_FETCH_COMPANINES);
        setLoading(false);
      });
  };

  useEffect(() => {
    getCompanies();
    if (company_uuid && template_type) {
      paramsFormHandler();
    }
  }, []);

  const shifts = [
    { name: "Shift A", machine: "82%", product: "79%" },
    { name: "Shift B", machine: "76%", product: "74%" },
  ];

  return (
    <div className="pl-2 py-2 h-full flex flex-col">
      <div className="flex flex-row justify-between pb-4">
        <PageBreadcrumb />
      </div>
      <div className="h-full rounded-xl overflow-hidden bg-white flex-1 overflow-y-auto  pb-5 min-w-full">
        <Spin spinning={loading}>
          <Form form={form}>
            <div className="w-fit px-8 py-6">
              <HeaderSelector
                filter={filter}
                config={config}
                form={form}
                setMachinesSummary={setMachinesSummary}
                setFilters={setFilters}
                setConfig={setConfig}
                getSummary={() =>
                  getSummary({
                    type: get(filter, "templateType"),
                    company_uuid: get(filter, "company.id"),
                    date: get(filter, "date").format("YYYY-MM-DD"),
                  })
                }
              />
            </div>
          </Form>

          {get(get(machinesSummary, "data", []), "length") > 0 && (
            <div>
              <div className="w-full px-8 overflow-y-auto">
                <div className="flex justify-between items-center ">
                  <div className="flex  flex-wrap gap-4 w-[100%] justify-around">
                    <OverallStats shiftMeta={get(machinesSummary, "meta")} />
                  </div>

                  <div className="flex justify-end gap-4 items-center">
                    <Tooltip className="float-right" title="Download Report">
                      <Button
                        type="primary"
                        icon={<DownloadOutlined />}
                        onClick={() => onDownloadReport()}
                      />
                    </Tooltip>
                    <Tooltip title="Export Summary Report Excel">
                      <ExcelExportButton onDownload={onDownloadExcelReport} />
                    </Tooltip>
                  </div>
                </div>

                <div className="mt-10">
                  {map(get(machinesSummary, "data", []), (summary, index) => (
                    <div className="w-full table-container" key={index}>
                      <Space className="my-4 w-full flex justify-start items-center gap-2">
                        <Title level={5} className="!mb-0 text-center">
                          {get(summary, "name")}
                        </Title>

                        <Tag>Last Synced : {get(summary, "last_synced")}</Tag>
                      </Space>
                      <TimeSummaryTable
                        className="mb-20 custom-horizontal-scrollbar"
                        data={summary}
                        filter={filter}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </Spin>
      </div>
    </div>
  );
};

export default requireAuth(SummaryWiseReportsScreen, [
  ...SUPER_ADMIN_ROLE_ACCESS,
  ...ORGANISATION_ADMIN_ROLE_IDs,
  ...ORGANISATION_MANAGER_IDs,
  ...ORGANISATION_SUPERVISORS_IDs
]);
